import {
  Card,
  Text,
  Group,
  Badge,
  Table,
} from '@mantine/core';
import {
  IconTrendingDown,
  IconTrendingUp,
  IconCheck,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatNumberWithCommas } from '@/helpers/string';
import { roundNumber } from '@/helpers/number';
import classes from './totalsComparison.module.css';

const TotalsComparison = ({ totalsBeforeCalculation, totalsAfterCalculation }) => {
  const { t } = useTranslation();

  const roundTotals = (totals) => {
    if (!totals) return null;

    const roundOrOriginal = (v) => {
      if (v == null) return null;
      const r = roundNumber(v);
      return Number.isFinite(r) ? r : v;
    };

    const toDecimal = (v, d = 2) => {
      if (v == null) return null;
      return Number.isFinite(v) ? Number(v.toFixed(d)) : v;
    };

    return {
      ...totals,
      totalAmount: roundOrOriginal(totals.totalAmount),
      monthsLeft: roundOrOriginal(totals.monthsLeft),
      newMonthlyPayment: roundOrOriginal(totals.newMonthlyPayment),
      maxMonthlyPayment: roundOrOriginal(totals.maxMonthlyPayment),
      finalPayment: roundOrOriginal(totals.finalPayment),
      finalInterestRepayment: roundOrOriginal(totals.finalInterestRepayment),
      finalLinkageRepayment: roundOrOriginal(totals.finalLinkageRepayment),
      returnConversion: toDecimal(totals.returnConversion),
    };
  };

  const roundTotalsBefore = roundTotals(totalsBeforeCalculation);
  const roundTotalsAfter = roundTotals(totalsAfterCalculation);

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return '-';
    }
    if (typeof value === 'number') {
      // For returnConversion, show 2 decimal places
      if (key === 'returnConversion') {
        return value.toFixed(2);
      }
      return formatNumberWithCommas(roundNumber(value));
    }
    return value;
  };

  const getImprovementIcon = (before, after, isLowerBetter = true) => {
    if (before === after) return null;

    const improved = isLowerBetter ? after < before : after > before;
    //  For fields where higher is better
    // (isLowerBetter = false), we want to show trending up for improvements
    if (isLowerBetter) {
      return improved ? (
        <IconTrendingDown size={16} color="green" />
      ) : (
        <IconTrendingUp size={16} color="red" />
      );
    }
    return improved ? (
      <IconTrendingUp size={16} color="green" />
    ) : (
      <IconTrendingDown size={16} color="red" />
    );
  };

  const getImprovementColor = (before, after, isLowerBetter = true) => {
    if (before === after) return 'gray';

    const improved = isLowerBetter ? after < before : after > before;
    return improved ? 'green' : 'red';
  };

  const getBeforeColor = (before, after) => {
    if (before === after) return 'gray';

    return 'black';
  };

  const getImprovementText = (before, after, isLowerBetter = true, withAmount = true) => {
    if (before === after) return '';
    const improved = isLowerBetter ? after < before : after > before;
    const difference = Math.abs(after - before);
    const percentage = ((difference / before) * 100).toFixed(1);

    if (improved) {
      // eslint-disable-next-line no-nested-ternary
      return isLowerBetter
        ? withAmount
          ? t('components.totalsComparison.decreasedBy', { amount: formatValue(difference, 'difference'), percentage })
          : t('components.totalsComparison.decreasedByWithoutAmount', { percentage })
        : withAmount
          ? t('components.totalsComparison.increasedBy', { amount: formatValue(difference, 'difference'), percentage })
          : t('components.totalsComparison.increasedByWithoutAmount', { percentage });
    }

    // eslint-disable-next-line no-nested-ternary
    return isLowerBetter
      ? withAmount
        ? t('components.totalsComparison.increasedBy', { amount: formatValue(difference, 'difference'), percentage })
        : t('components.totalsComparison.increasedByWithoutAmount', { percentage })
      : withAmount
        ? t('components.totalsComparison.decreasedBy', { amount: formatValue(difference, 'difference'), percentage })
        : t('components.totalsComparison.decreasedByWithoutAmount', { percentage });
  };

  const comparisonFields = [
    {
      key: 'totalAmount',
      label: t('components.totalsComparison.totalAmount'),
      isLowerBetter: false, // Higher is better for total amount
      suffix: ' ₪',
    },
    {
      key: 'monthsLeft',
      label: t('components.totalsComparison.loanPeriod'),
      isLowerBetter: false,
      suffix: ' חודשים',
    },
    {
      key: 'newMonthlyPayment',
      label: t('components.totalsComparison.monthlyPayment'),
      isLowerBetter: true, // Lower is better for monthly payment
      suffix: ' ₪',
    },
    {
      key: 'maxMonthlyPayment',
      label: t('components.totalsComparison.maxMonthlyPayment'),
      isLowerBetter: true, // Lower is better for max monthly payment
      suffix: ' ₪',
    },
    {
      key: 'finalPayment',
      label: t('components.totalsComparison.finalPayment'),
      isLowerBetter: true, // Lower is better for final payment
      suffix: ' ₪',
    },
    {
      key: 'finalInterestRepayment',
      label: t('components.totalsComparison.interestRepayment'),
      isLowerBetter: true, // Lower is better for interest
      suffix: ' ₪',
      fallbackKey: 'finalInterestRepayment', // Use this as fallback if interestRepayment is not available
    },
    {
      key: 'finalLinkageRepayment',
      label: t('components.totalsComparison.linkageRepayment'),
      isLowerBetter: true, // Lower is better for linkage
      suffix: ' ₪',
      fallbackKey: 'finalLinkageRepayment',
    },
    {
      key: 'returnConversion',
      label: t('components.totalsComparison.returnConversion'),
      isLowerBetter: true, // Lower is better for return conversion
      suffix: '',
    },
  ];

  // Check if data is available
  if (!totalsBeforeCalculation || !totalsAfterCalculation) {
    return (
      <Card withBorder p="xl" radius="md">
        <Group mb="lg">
          <IconCheck size={24} color="var(--mantine-color-blue-6)" />
          <Text size="lg" fw={600}>
            {t('components.totalsComparison.title')}
          </Text>
        </Group>
        <Text ta="center" c="dimmed" py="xl">
          {t('components.totalsComparison.noDataAvailable')}
        </Text>
      </Card>
    );
  }

  return (
    <>
      <div className={classes.tableContainer}>
        <Table striped withColumnBorders classNames={{ tr: `${classes.changeColor} ${classes.tableBody}`, th: classes.changeColor, td: classes.changeColor }}>
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th></Table.Th>
              <Table.Th>{t('components.totalsComparison.before')}</Table.Th>
              <Table.Th>{t('components.totalsComparison.after')}</Table.Th>
              <Table.Th>{t('components.totalsComparison.improvement')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {comparisonFields
              .filter((field) => {
                // Filter out monthsLeft if it doesn't exist in either dataset
                if (field.key === 'monthsLeft') {
                  const beforeValue = roundTotalsBefore[field.key];
                  const afterValue = roundTotalsAfter[field.key];
                  return (beforeValue !== null && beforeValue !== undefined && beforeValue !== '')
                    || (afterValue !== null && afterValue !== undefined && afterValue !== '');
                }
                return true; // Show all other fields
              })
              .map((field) => {
                // Handle fallback logic for interestRepayment
                const getValue = (data, key, fallbackKey) => {
                  if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
                    return data[key];
                  }
                  if (fallbackKey && data[fallbackKey] !== null && data[fallbackKey] !== undefined && data[fallbackKey] !== '') {
                    return data[fallbackKey];
                  }
                  return data[key]; // Return original value (could be null/undefined)
                };

                const withAmount = field.key !== 'returnConversion';
                const beforeValue = getValue(roundTotalsBefore, field.key, field.fallbackKey);
                const afterValue = getValue(roundTotalsAfter, field.key, field.fallbackKey);
                const { isLowerBetter } = field;

                const improvementColor = getImprovementColor(
                  beforeValue,
                  afterValue,
                  isLowerBetter,
                );
                const beforeColor = getBeforeColor(
                  beforeValue,
                  afterValue,
                );
                const improvementIcon = getImprovementIcon(
                  beforeValue,
                  afterValue,
                  isLowerBetter,
                );
                const improvementText = getImprovementText(
                  beforeValue,
                  afterValue,
                  isLowerBetter,
                  withAmount,
                );

                return (
                  <Table.Tr key={field.key}>
                    <Table.Td className={classes.highlightCell}>
                      <Text fw={500}>
                        {field.label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500} c={beforeColor}>
                        {formatValue(beforeValue, field.key) === '-' ? '-' : `${formatValue(beforeValue, field.key)}${field.suffix}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500} c={improvementColor}>
                        {formatValue(afterValue, field.key) === '-' ? '-' : `${formatValue(afterValue, field.key)}${field.suffix}`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {beforeValue !== afterValue && (
                        <Group gap="xs" align="center">
                          <Badge
                            size="lg"
                            radius="sm"
                            color={improvementColor}
                            variant="light"
                          >
                            <Group spacing={4} align="center" noWrap>
                              {improvementIcon}
                              <Text>{improvementText}</Text>
                            </Group>
                          </Badge>
                        </Group>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
          </Table.Tbody>
        </Table>
      </div>
    </>
  );
};

TotalsComparison.propTypes = {
  totalsBeforeCalculation: PropTypes.object.isRequired,
  totalsAfterCalculation: PropTypes.object.isRequired,
};

export default TotalsComparison;
